import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ClipboardList, Plus, X, Loader2, Clock, Users,
  Play, Copy, Edit2, Trash2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function PracticePlanBuilder({ teamId }) {
  const [templates, setTemplates] = useState([]);
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'training',
    duration_minutes: 45,
    age_groups: ['all'],
    description: '',
    exercises: []
  });

  const [newExercise, setNewExercise] = useState({
    name: '',
    duration: '',
    notes: ''
  });

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/practice-plans/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
      setSystemTemplates(response.data.system_templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/practice-plans/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const createTemplate = async () => {
    if (!newTemplate.name) {
      toast.error('Template name is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/practice-plans/templates`, newTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template created');
      setShowCreateModal(false);
      setNewTemplate({
        name: '',
        category: 'training',
        duration_minutes: 45,
        age_groups: ['all'],
        description: '',
        exercises: []
      });
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to create template');
    }
  };

  const applyTemplate = async (template) => {
    if (!teamId) {
      toast.error('Please select a team first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      await axios.post(`${API}/practice-plans/sessions`, {
        template_id: template.id,
        team_id: teamId,
        date: today,
        start_time: '10:00'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session created from template');
    } catch (err) {
      toast.error('Failed to create session');
    }
  };

  const addExercise = () => {
    if (!newExercise.name || !newExercise.duration) {
      toast.error('Exercise name and duration are required');
      return;
    }
    setNewTemplate({
      ...newTemplate,
      exercises: [...newTemplate.exercises, {...newExercise}]
    });
    setNewExercise({ name: '', duration: '', notes: '' });
  };

  const removeExercise = (index) => {
    setNewTemplate({
      ...newTemplate,
      exercises: newTemplate.exercises.filter((_, i) => i !== index)
    });
  };

  const getCategoryColor = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat?.color || '#6b7280';
  };

  const allTemplates = [...systemTemplates, ...templates];
  const filteredTemplates = activeCategory === 'all' 
    ? allTemplates 
    : allTemplates.filter(t => t.category === activeCategory);

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6" data-testid="practice-plan-builder">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center">
            <ClipboardList className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Practice Plan Templates</h3>
            <p className="text-xs text-zinc-500">Pre-built training sessions</p>
          </div>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm"
          data-testid="create-template-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          Create Template
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-all ${
            activeCategory === 'all'
              ? 'bg-white text-black'
              : 'bg-zinc-800 text-zinc-400 hover:text-white'
          }`}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            style={{ 
              backgroundColor: activeCategory === cat.id ? cat.color : undefined,
              color: activeCategory === cat.id ? 'white' : undefined
            }}
            className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-all ${
              activeCategory !== cat.id ? 'bg-zinc-800 text-zinc-400 hover:text-white' : ''
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Templates List */}
      <div className="space-y-2 max-h-[350px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No templates found</p>
          </div>
        ) : (
          filteredTemplates.map(template => (
            <div
              key={template.id}
              className="p-4 bg-zinc-800/50 rounded-xl hover:bg-zinc-800 transition-all border border-zinc-700/50 cursor-pointer"
              onClick={() => setSelectedTemplate(template)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-white">{template.name}</p>
                    {template.is_system && (
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">System</span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{template.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {template.duration_minutes} min
                    </span>
                    <span 
                      className="px-2 py-0.5 rounded"
                      style={{ backgroundColor: `${getCategoryColor(template.category)}30`, color: getCategoryColor(template.category) }}
                    >
                      {template.category}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); applyTemplate(template); }}
                  className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                >
                  <Play className="h-4 w-4 mr-1" />
                  Use
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Template Detail Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedTemplate(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-white">{selectedTemplate.name}</h3>
                <p className="text-sm text-zinc-500">{selectedTemplate.duration_minutes} minutes</p>
              </div>
              <button onClick={() => setSelectedTemplate(null)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-zinc-400 mb-4">{selectedTemplate.description}</p>

            <h4 className="text-sm font-medium text-zinc-400 mb-2">Exercises</h4>
            <div className="space-y-2">
              {selectedTemplate.exercises?.map((ex, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-white font-medium">{ex.name}</p>
                    <p className="text-xs text-zinc-500">{ex.duration}</p>
                    {ex.notes && <p className="text-xs text-zinc-400 mt-1">{ex.notes}</p>}
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={() => { applyTemplate(selectedTemplate); setSelectedTemplate(null); }}
              className="w-full mt-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
            >
              <Play className="h-4 w-4 mr-2" />
              Use This Template
            </Button>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Create Template</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                placeholder="Template Name"
                value={newTemplate.name}
                onChange={e => setNewTemplate({...newTemplate, name: e.target.value})}
                className="bg-zinc-800 border-zinc-700 text-white"
              />

              <select
                value={newTemplate.category}
                onChange={e => setNewTemplate({...newTemplate, category: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>

              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Duration (minutes)</label>
                <Input
                  type="number"
                  value={newTemplate.duration_minutes}
                  onChange={e => setNewTemplate({...newTemplate, duration_minutes: parseInt(e.target.value) || 45})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <textarea
                placeholder="Description"
                value={newTemplate.description}
                onChange={e => setNewTemplate({...newTemplate, description: e.target.value})}
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white resize-none"
              />

              {/* Exercises */}
              <div>
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Exercises</h4>
                {newTemplate.exercises.map((ex, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2 p-2 bg-zinc-800/50 rounded-lg">
                    <span className="text-xs text-zinc-500 w-5">{idx + 1}.</span>
                    <span className="flex-1 text-white text-sm">{ex.name} ({ex.duration})</span>
                    <button onClick={() => removeExercise(idx)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                <div className="grid grid-cols-3 gap-2 mt-2">
                  <Input
                    placeholder="Exercise name"
                    value={newExercise.name}
                    onChange={e => setNewExercise({...newExercise, name: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm col-span-2"
                  />
                  <Input
                    placeholder="5 min"
                    value={newExercise.duration}
                    onChange={e => setNewExercise({...newExercise, duration: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  />
                </div>
                <Input
                  placeholder="Notes (optional)"
                  value={newExercise.notes}
                  onChange={e => setNewExercise({...newExercise, notes: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm mt-2"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addExercise}
                  className="w-full mt-2 text-emerald-400 hover:bg-emerald-500/20"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Exercise
                </Button>
              </div>

              <Button
                onClick={createTemplate}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
              >
                Create Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
